import { useState } from 'react'
import { Link } from 'react-router-dom'

import { usePlayerAuth } from '../contexts/PlayerAuthContext'
import { useStats } from '../contexts/StatsContext'
import { getSavePromptDismissedAt, setSavePromptDismissedAt } from '../utils/storage'
import { shouldShowSavePrompt } from '../utils/savePrompt'
import { savePromptCopy } from '../utils/savePromptCopy'

interface SaveProgressPromptProps {
  /** The prompt only appears after a win: the moment there is something worth keeping. */
  won: boolean
}

// Shown on the results screens to guests. Never blocks anything, is one click to
// dismiss, and stays quiet for a few days after a dismissal (utils/savePrompt).
export function SaveProgressPrompt({ won }: SaveProgressPromptProps) {
  const { user, initializing, available } = usePlayerAuth()
  const { data: stats } = useStats()
  // Read once when the page opens; the clock is not read during render.
  const [openedAt] = useState(() => Date.now())
  const [lastDismissedAt] = useState(() => getSavePromptDismissedAt())
  // Hides the card straight away when "Not now" is clicked, whatever the stored time says.
  const [dismissedNow, setDismissedNow] = useState(false)

  if (dismissedNow || !available) return null
  if (!shouldShowSavePrompt({ signedIn: user !== null, initializing, won, lastDismissedAt, now: openedAt })) {
    return null
  }

  const copy = savePromptCopy(stats?.streak.current ?? 0)

  const dismiss = () => {
    setDismissedNow(true)
    setSavePromptDismissedAt(Date.now())
  }

  return (
    <div data-testid="save-prompt" className="my-3 w-full max-w-md rounded-2xl border border-purple-500/35 bg-purple-500/10 p-4 text-left">
      <p data-testid="save-prompt-title" className="font-bold text-white">
        {copy.title}
      </p>
      <p data-testid="save-prompt-body" className="mb-3 mt-1 text-sm text-white/75">
        {copy.body}
      </p>
      <div className="flex items-center gap-4">
        <Link
          to="/account"
          data-testid="save-prompt-signin"
          className="rounded-lg bg-gradient-to-r from-[#b01018] to-[#ed1c24] px-4 py-2 text-sm font-semibold text-white">
          Sign in
        </Link>
        <button type="button" data-testid="save-prompt-dismiss" onClick={dismiss} className="text-sm text-white/60 hover:text-white">
          Not now
        </button>
      </div>
    </div>
  )
}
